/**
 * Name Filter Definition
 *
 * Filters nodes by their name (multi-language aware with phonetic matching)
 * Uses the same phonetic search utilities as the main search functionality.
 */

import { GraphDataNode } from '../../types/graph.types';
import {
  FilterDefinition,
  FilterOperator,
  FilterValueType,
  FilterSuggestion,
  FilterValue,
} from '../types';
import { quickSearch } from '../../utils/search';

/**
 * Remove diacritics from a string for comparison (e.g., 'śrī' -> 'sri')
 */
function removeDiacritics(text: string): string {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Get the searchable name from a node
 */
function getNodeSearchableName(node: GraphDataNode): string {
  const d = node.data ?? {};
  // node.data is Record<string, unknown> in the new typing — coerce known
  // string fields, default to ''.
  const pick = (k: string): string =>
    typeof (d as Record<string, unknown>)[k] === 'string'
      ? ((d as Record<string, string>)[k])
      : '';
  return pick('enNormalizedName') || pick('enName') || pick('teName') || pick('name') || '';
}

/**
 * Name Filter Definition
 */
export const nameFilter: FilterDefinition = {
  id: 'name',
  label: 'Name',
  shortLabel: 'Name',
  icon: 'DocumentTextIcon',
  valueType: FilterValueType.String,
  supportedOperators: [
    FilterOperator.SoundsLike,
    FilterOperator.Contains,
    FilterOperator.StartsWith,
    FilterOperator.EndsWith,
    FilterOperator.Is,
    FilterOperator.NotContains,
  ],
  defaultOperator: FilterOperator.SoundsLike,
  placeholder: 'Enter name to search...',

  suggestMatch(query: string, nodes: GraphDataNode[]): FilterSuggestion[] {
    const trimmedQuery = query.trim();
    if (!trimmedQuery || trimmedQuery.length < 2) {
      return [];
    }

    // Skip if query is purely numeric (let nodeIdFilter handle it)
    if (/^\d+$/.test(trimmedQuery)) {
      return [];
    }

    const normalizedQuery = removeDiacritics(trimmedQuery);
    // Use lowercase for the filter value to ensure case-insensitive matching
    const lowercaseQuery = trimmedQuery.toLowerCase();
    const suggestions: FilterSuggestion[] = [];

    // Count matches for different operators (text-based and phonetic)
    let containsCount = 0;
    let startsWithCount = 0;
    let exactCount = 0;
    let phoneticOnlyCount = 0;

    // Sample matching nodes for descriptions
    const containsMatches: string[] = [];
    const startsWithMatches: string[] = [];
    const phoneticMatches: string[] = [];

    // Track which nodes matched via text vs phonetic
    const textMatchedIds = new Set<number>();

    for (const node of nodes) {
      const name = getNodeSearchableName(node);
      if (!name) continue;

      const normalizedName = removeDiacritics(name);
      let matchedByText = false;

      if (normalizedName === normalizedQuery) {
        exactCount++;
        matchedByText = true;
      }

      if (normalizedName.startsWith(normalizedQuery)) {
        startsWithCount++;
        if (startsWithMatches.length < 3) {
          startsWithMatches.push(name);
        }
        matchedByText = true;
      }

      if (normalizedName.includes(normalizedQuery)) {
        containsCount++;
        if (containsMatches.length < 3) {
          containsMatches.push(name);
        }
        matchedByText = true;
      }

      if (matchedByText) {
        textMatchedIds.add(node.nodeId);
      }
    }

    // Second pass: find phonetic-only matches using the proper search utility
    const phoneticResults = quickSearch(
      nodes.filter(n => !textMatchedIds.has(n.nodeId)),
      trimmedQuery
    );

    for (const node of phoneticResults) {
      const name = getNodeSearchableName(node);
      if (!name) continue;

      phoneticOnlyCount++;
      if (phoneticMatches.length < 3) {
        phoneticMatches.push(name);
      }
    }

    // Add "sounds like" suggestion first (highest priority)
    // Always show if query is long enough, as phonetic search is the most flexible
    if (trimmedQuery.length >= 3) {
      const examples = phoneticMatches.slice(0, 2).join(', ');
      const totalPhonetic = containsCount + phoneticOnlyCount;
      suggestions.push({
        filterId: 'name',
        label: `Name: sounds like "${trimmedQuery}"`,
        description: totalPhonetic > 0
          ? `${totalPhonetic} match${totalPhonetic !== 1 ? 'es' : ''}: ${examples}${totalPhonetic > 2 ? '...' : ''}`
          : 'Search by pronunciation',
        prefilledOperator: FilterOperator.SoundsLike,
        prefilledValue: lowercaseQuery,
        needsConfiguration: false,
        score: 100, // Highest priority
      });
    }

    // Count ends with matches
    let endsWithCount = 0;
    const endsWithMatches: string[] = [];
    for (const node of nodes) {
      const name = getNodeSearchableName(node);
      if (!name) continue;
      const normalizedName = removeDiacritics(name);
      if (normalizedName.endsWith(normalizedQuery)) {
        endsWithCount++;
        if (endsWithMatches.length < 3) {
          endsWithMatches.push(name);
        }
      }
    }

    // Add "contains" suggestion (second priority) - always show
    {
      const examples = containsMatches.slice(0, 2).join(', ');
      suggestions.push({
        filterId: 'name',
        label: `Name: contains "${trimmedQuery}"`,
        description: containsCount > 0
          ? `${containsCount} match${containsCount !== 1 ? 'es' : ''}: ${examples}${containsCount > 2 ? '...' : ''}`
          : 'Search for names containing this text',
        prefilledOperator: FilterOperator.Contains,
        prefilledValue: lowercaseQuery,
        needsConfiguration: false,
        score: 90, // Second priority
      });
    }

    // Add "starts with" suggestion (third priority) - always show
    {
      const examples = startsWithMatches.slice(0, 2).join(', ');
      suggestions.push({
        filterId: 'name',
        label: `Name: starts with "${trimmedQuery}"`,
        description: startsWithCount > 0
          ? `${startsWithCount} match${startsWithCount !== 1 ? 'es' : ''}: ${examples}${startsWithCount > 2 ? '...' : ''}`
          : 'Search for names starting with this text',
        prefilledOperator: FilterOperator.StartsWith,
        prefilledValue: lowercaseQuery,
        needsConfiguration: false,
        score: 80, // Third priority
      });
    }

    // Add "ends with" suggestion (fourth priority) - always show
    {
      const examples = endsWithMatches.slice(0, 2).join(', ');
      suggestions.push({
        filterId: 'name',
        label: `Name: ends with "${trimmedQuery}"`,
        description: endsWithCount > 0
          ? `${endsWithCount} match${endsWithCount !== 1 ? 'es' : ''}: ${examples}${endsWithCount > 2 ? '...' : ''}`
          : 'Search for names ending with this text',
        prefilledOperator: FilterOperator.EndsWith,
        prefilledValue: lowercaseQuery,
        needsConfiguration: false,
        score: 70, // Fourth priority
      });
    }

    // Add "is exactly" suggestion (fifth priority) - always show
    suggestions.push({
      filterId: 'name',
      label: `Name: is exactly "${trimmedQuery}"`,
      description: exactCount > 0
        ? `${exactCount} exact match${exactCount !== 1 ? 'es' : ''}`
        : 'Search for exact name match',
      prefilledOperator: FilterOperator.Is,
      prefilledValue: lowercaseQuery,
      needsConfiguration: false,
      score: 60, // Fifth priority
    });

    // Add "does not contain" suggestion (sixth priority) - always show
    suggestions.push({
      filterId: 'name',
      label: `Name: does not contain "${trimmedQuery}"`,
      description: 'Exclude names containing this text',
      prefilledOperator: FilterOperator.NotContains,
      prefilledValue: lowercaseQuery,
      needsConfiguration: false,
      score: 50, // Sixth priority
    });

    return suggestions.sort((a, b) => b.score - a.score);
  },

  execute(node: GraphDataNode, operator: FilterOperator, value: FilterValue): boolean {
    const filterValue = String(value).toLowerCase();
    if (!filterValue) {
      return true;
    }

    // Get all name fields to check (enNormalizedName and name are the primary ones)
    const namesToCheck = [
      node.data?.enNormalizedName,
      node.data?.name,
      node.data?.enName,
      node.data?.teName,
    ].filter(Boolean) as string[];

    if (namesToCheck.length === 0) {
      return false;
    }

    // Normalize filter value
    const normalizedFilter = removeDiacritics(filterValue);

    // For SoundsLike, use the proper phonetic search from search.ts
    if (operator === FilterOperator.SoundsLike) {
      // Use quickSearch which handles phonetic matching properly
      const matches = quickSearch([node], filterValue);
      return matches.length > 0;
    }

    // For text-based operators, check each name field
    for (const name of namesToCheck) {
      const normalizedName = removeDiacritics(name);

      switch (operator) {
        case FilterOperator.Contains:
          if (normalizedName.includes(normalizedFilter)) {
            return true;
          }
          break;

        case FilterOperator.Is:
          if (normalizedName === normalizedFilter) {
            return true;
          }
          break;

        case FilterOperator.StartsWith:
          if (normalizedName.startsWith(normalizedFilter)) {
            return true;
          }
          break;

        case FilterOperator.EndsWith:
          if (normalizedName.endsWith(normalizedFilter)) {
            return true;
          }
          break;

        case FilterOperator.NotContains:
          // For "not contains", ALL name fields must not contain the value
          // So we check the opposite and return early if found
          if (normalizedName.includes(normalizedFilter)) {
            return false;
          }
          break;

        default:
          return true;
      }
    }

    // For "not contains", if we get here, none of the fields contained the value
    if (operator === FilterOperator.NotContains) {
      return true;
    }

    return false;
  },

  getDisplayValue(value: FilterValue): string {
    const strValue = String(value);
    // Truncate long values for display
    if (strValue.length > 20) {
      return `${strValue.substring(0, 17)}...`;
    }
    return strValue;
  },
};
